import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Panel } from '@/components/Panel';
import { useAuth } from '@/contexts/AuthContext';
import { startImpersonation } from '@/lib/impersonate';
import { toast } from 'sonner';
import { UserCog, Trash2, CreditCard, Mail } from 'lucide-react';

export function AdminDashboard() {
 const [d, setD] = useState(null);
 useEffect(() => { (async () => { const { data } = await api.get('/admin/dashboard'); setD(data); })(); }, []);
 if (!d) return <div style={{ color: 'var(--heritage-plum)' }}>Loading…</div>;
 const cards = [
 { label: 'Members', v: d.members },
 { label: 'Founders', v: d.founders },
 { label: 'Applications pending', v: d.applications_pending },
 { label: 'Payments pending', v: d.payments_pending },
 { label: 'Orders in production', v: d.orders_in_production },
 { label: 'Confirmed revenue (NZD)', v: `$${d.revenue_confirmed?.toFixed?.(0) ?? 0}` },
 ];
 const funnel = d.funnel || {};
 const stages = [
 { key: 'applied', label: 'Applied', v: funnel.applied || 0, hint: 'Total applications submitted' },
 { key: 'picked_tier', label: 'Picked tier', v: funnel.picked_tier || 0, hint: 'Chose a founders tier' },
 { key: 'reserved_payment', label: 'Reserved payment', v: funnel.reserved_payment || 0, hint: 'Have a payment invoice' },
 { key: 'approved', label: 'Approved', v: funnel.approved || 0, hint: 'Admin approved' },
 { key: 'paid', label: 'Paid', v: funnel.paid || 0, hint: 'Payment verified' },
 ];
 const topValue = Math.max(1, ...stages.map((s) => s.v));
 const rate = (from, to) => (from > 0 ? Math.round((to / from) * 100) : 0);
 return (
 <div className="space-y-6" data-testid="admin-dashboard">
 <Panel className="p-8">
 <div className="h-eyebrow">Overview</div>
 <div className="h-display text-4xl mt-1" style={{ color: 'var(--heritage-plum)' }}>Heritage. today</div>
 </Panel>
 <div className="grid md:grid-cols-3 gap-6">
 {cards.map(c => (
 <Panel key={c.label} className="p-6">
 <div className="h-eyebrow">{c.label}</div>
 <div className="h-serif text-4xl mt-1" style={{ color: 'var(--heritage-plum)' }}>{c.v}</div>
 </Panel>
 ))}
 </div>

 <Panel className="p-6" data-testid="admin-funnel">
   <div className="flex items-baseline justify-between flex-wrap gap-2">
     <div>
       <div className="h-eyebrow">Founders funnel</div>
       <div className="h-serif text-2xl mt-1" style={{ color: 'var(--heritage-plum)' }}>Applications to paid</div>
     </div>
     <div className="text-xs opacity-70" style={{ color: 'var(--heritage-plum)' }}>
       Overall conversion: <span className="h-serif">{rate(funnel.applied || 0, funnel.paid || 0)}%</span>
       {funnel.rejected ? <span className="ml-3">Rejected: <span className="h-serif">{funnel.rejected}</span></span> : null}
     </div>
   </div>
   <div className="mt-5 space-y-3">
     {stages.map((s, i) => {
       const prev = i > 0 ? stages[i - 1].v : s.v;
       const conv = i > 0 ? rate(prev, s.v) : 100;
       const pct = Math.round((s.v / topValue) * 100);
       return (
         <div key={s.key} className="grid grid-cols-[120px,1fr,90px] items-center gap-3" data-testid={`funnel-stage-${s.key}`}>
           <div>
             <div className="h-eyebrow text-[10px]">{s.label}</div>
             <div className="text-[10px] opacity-60 leading-tight" style={{ color: 'var(--heritage-plum)' }}>{s.hint}</div>
           </div>
           <div className="relative h-8 rounded-full overflow-hidden" style={{ background: 'rgba(75,24,56,0.08)' }}>
             <div className="absolute inset-y-0 left-0 rounded-full flex items-center px-3"
                  style={{ width: `${Math.max(pct, 4)}%`, background: 'var(--heritage-plum)', minWidth: '28px' }}>
               <span className="h-serif text-sm" style={{ color: 'var(--heritage-cream)' }}>{s.v}</span>
             </div>
           </div>
           <div className="text-right">
             {i > 0 && (
               <div className="text-xs h-serif" style={{ color: conv >= 60 ? '#5B6E2C' : (conv >= 30 ? 'var(--heritage-plum)' : '#9A2C2C') }}>
                 {conv}%
               </div>
             )}
             {i > 0 && <div className="text-[10px] opacity-60" style={{ color: 'var(--heritage-plum)' }}>step conv.</div>}
           </div>
         </div>
       );
     })}
   </div>
 </Panel>
 </div>
 );
}

export function AdminMembers() {
 const [rows, setRows] = useState([]);
 const navigate = useNavigate();
 const { refresh } = useAuth();
 useEffect(() => { (async () => { const { data } = await api.get('/admin/members'); setRows(data.members || []); })(); }, []);
 const viewAs = async (uid) => {
   try { await startImpersonation(uid, { navigate, refreshAuth: refresh }); }
   catch (e) { alert('Could not impersonate this user.'); }
 };
 return (
 <Panel className="p-6">
 <div className="h-eyebrow mb-3">Members</div>
 <div className="overflow-x-auto">
 <table className="w-full text-sm">
 <thead><tr className="text-left h-eyebrow"><th className="py-2">Name</th><th>Email</th><th>Role</th><th>Since</th><th></th></tr></thead>
 <tbody style={{ color: 'var(--heritage-plum)' }}>
 {rows.map(r => (
 <tr key={r.id} className="border-t" style={{ borderColor: 'rgba(75,24,56,0.1)' }}>
 <td className="py-2 h-serif">{r.name}</td><td>{r.email}</td><td className="capitalize">{r.role}</td><td>{(r.created_at || '').slice(0,10)}</td>
 <td>{r.role !== 'admin' && <button className="h-btn-outline text-xs" onClick={() => viewAs(r.id)} data-testid={`impersonate-${r.id}`}><UserCog size={14} className="mr-1.5" /> View as</button>}</td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 </Panel>
 );
}

export function AdminApplications() {
 const [rows, setRows] = useState([]);
 const [tiers, setTiers] = useState([]);
 const [busy, setBusy] = useState(null);
 const [filter, setFilter] = useState('all');
 const [invoiceDlg, setInvoiceDlg] = useState(null); // application obj
 const [invoice, setInvoice] = useState({ tier_id: '', plan_type: 'full' });
 const [purgeDlg, setPurgeDlg] = useState(false);
 const [purgePreview, setPurgePreview] = useState(null);

 const load = async () => {
   const [{ data }, { data: td }] = await Promise.all([
     api.get('/admin/applications'),
     api.get('/tiers').catch(() => ({ data: { tiers: [] } })),
   ]);
   setRows(data.applications || []);
   setTiers(td.tiers || []);
 };
 useEffect(() => { load(); }, []);

 const decide = async (id, approved, name) => {
   setBusy(id);
   try {
     const { data } = await api.post(`/admin/applications/${id}/decision`, { approved });
     if (approved) {
       const bits = [];
       if (data?.created_user) bits.push('customer added to Members');
       if (data?.created_payment) bits.push(`pending payment ${data.payment_reference || ''} created`);
       toast.success(`${name || 'Application'} approved${bits.length ? ' (' + bits.join(', ') + ')' : ''}`);
     } else {
       toast.success(`${name || 'Application'} rejected`);
     }
     await load();
   } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
   finally { setBusy(null); }
 };

 const removeApp = async (r) => {
   if (!confirm(`Delete ${r.name}'s application + all their data (user, payment, orders)? This cannot be undone.`)) return;
   try {
     await api.delete(`/admin/applications/${r.id}`);
     toast.success(`${r.name} deleted`);
     await load();
   } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
 };

 const openInvoice = (r) => {
   setInvoiceDlg(r);
   setInvoice({ tier_id: r.tier_id || tiers[0]?.id || '', plan_type: 'full' });
 };
 const submitInvoice = async () => {
   if (!invoice.tier_id) { toast.error('Pick a tier'); return; }
   try {
     const { data } = await api.post(`/admin/applications/${invoiceDlg.id}/create-invoice`, invoice);
     toast.success(`Invoice ${data.founder_number || data.payment?.reference} created`);
     setInvoiceDlg(null);
     await load();
   } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
 };

 const previewPurge = async () => {
   try {
     const { data } = await api.post('/admin/applications/purge-test', { only_test_domains: true, dry_run: true });
     setPurgePreview(data);
   } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
 };
 const doPurge = async () => {
   try {
     const { data } = await api.post('/admin/applications/purge-test', { only_test_domains: true, dry_run: false });
     toast.success(`Deleted ${data.deleted} test application${data.deleted === 1 ? '' : 's'} + related data`);
     setPurgeDlg(false); setPurgePreview(null);
     await load();
   } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
 };

 const filtered = rows.filter((r) => {
   if (filter === 'all') return true;
   const s = r.admin_status || r.status || '';
   if (filter === 'pending') return !r.admin_status || (s !== 'approved' && s !== 'rejected');
   if (filter === 'no-invoice') return r.admin_status === 'approved' && !r.payment_id;
   return s === filter;
 });
 const counts = {
   all: rows.length,
   pending: rows.filter((r) => !r.admin_status || (r.admin_status !== 'approved' && r.admin_status !== 'rejected')).length,
   approved: rows.filter((r) => r.admin_status === 'approved').length,
   'no-invoice': rows.filter((r) => r.admin_status === 'approved' && !r.payment_id).length,
   rejected: rows.filter((r) => r.admin_status === 'rejected').length,
 };
 return (
 <Panel className="p-6">
 <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
   <div className="h-eyebrow">Applications</div>
   <div className="flex flex-wrap items-center gap-2">
     {[['all','All'],['pending','Pending'],['approved','Approved'],['no-invoice','Approved • no invoice'],['rejected','Rejected']].map(([s,label]) => (
       <button key={s} onClick={() => setFilter(s)}
         className="rounded-full px-3 py-1 text-xs h-serif"
         style={{
           background: filter === s ? 'var(--heritage-plum)' : 'transparent',
           color: filter === s ? 'var(--heritage-cream)' : 'var(--heritage-plum)',
           border: '1px solid var(--heritage-plum)',
         }}
         data-testid={`applications-filter-${s}`}>
         {label} <span className="opacity-70">({counts[s] || 0})</span>
       </button>
     ))}
     <button onClick={() => { setPurgeDlg(true); previewPurge(); }}
       className="h-btn-outline text-xs" style={{ borderColor: '#9A2C2C', color: '#9A2C2C' }}
       data-testid="applications-purge-test">
       <Trash2 size={12} className="mr-1" /> Purge test data
     </button>
   </div>
 </div>
 <div className="space-y-3">
 {filtered.map(r => {
   const status = r.admin_status || r.status;
   const isApproved = r.admin_status === 'approved';
   const isRejected = r.admin_status === 'rejected';
   const noInvoice = isApproved && !r.payment_id;
   return (
     <div key={r.id} className="p-4 rounded-lg" style={{ background: '#FBF8F1', border: '1px solid rgba(75,24,56,0.15)' }} data-testid={`application-row-${r.id}`}>
       <div className="flex justify-between items-start flex-wrap gap-3">
         <div className="min-w-0">
           <div className="h-serif text-lg" style={{ color: 'var(--heritage-plum)' }}>{r.name} · {r.email}</div>
           <div className="text-xs opacity-80" style={{ color: 'var(--heritage-plum)' }}>{r.phone} • Size {r.clothing_size} • {r.address}</div>
           <div className="h-eyebrow mt-2 flex items-center gap-2 flex-wrap">
             <span>Status: {status}</span>
             {r.tier_name && <span>• {r.tier_name}</span>}
             {r.founder_number && <span>• {r.founder_number}</span>}
             {isApproved && <span className="rounded-full px-2 py-0.5 text-[10px]" style={{ background: 'rgba(91,110,44,0.15)' }}>APPROVED</span>}
             {isRejected && <span className="rounded-full px-2 py-0.5 text-[10px]" style={{ background: 'rgba(154,44,44,0.15)' }}>REJECTED</span>}
             {noInvoice && <span className="rounded-full px-2 py-0.5 text-[10px]" style={{ background: 'rgba(154,110,44,0.15)' }}>NO INVOICE</span>}
           </div>
         </div>
         <div className="flex flex-wrap gap-2 items-center">
           {noInvoice && (
             <button onClick={() => openInvoice(r)} className="h-btn text-xs" data-testid={`application-invoice-${r.id}`}>
               <CreditCard size={12} className="mr-1" /> Create invoice
             </button>
           )}
           <button className="h-btn text-xs disabled:opacity-50"
             onClick={() => decide(r.id, true, r.name)} disabled={busy === r.id || isApproved}
             data-testid={`application-approve-${r.id}`}>
             {busy === r.id ? 'Saving...' : (isApproved ? 'Approved' : 'Approve')}
           </button>
           <button className="h-btn-outline text-xs disabled:opacity-50"
             style={{ borderColor: '#9A2C2C', color: '#9A2C2C' }}
             onClick={() => decide(r.id, false, r.name)} disabled={busy === r.id || isRejected}
             data-testid={`application-reject-${r.id}`}>
             {busy === r.id ? '...' : (isRejected ? 'Rejected' : 'Reject')}
           </button>
           <button className="h-btn-outline text-xs" onClick={() => removeApp(r)}
             style={{ borderColor: '#9A2C2C', color: '#9A2C2C' }}
             data-testid={`application-delete-${r.id}`} title="Delete this application and all related data">
             <Trash2 size={12} />
           </button>
         </div>
       </div>
       <div className="mt-2 text-sm italic" style={{ color: 'var(--heritage-plum)' }}>“{r.reason}”</div>
     </div>
   );
 })}
 {filtered.length === 0 && <div className="text-sm opacity-70" style={{ color: 'var(--heritage-plum)' }}>No applications match this filter.</div>}
 </div>

 {invoiceDlg && (
   <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(75,24,56,0.5)' }} onClick={() => setInvoiceDlg(null)}>
     <div className="w-full max-w-md rounded-2xl p-6" onClick={(e) => e.stopPropagation()}
       style={{ background: '#FBF8F1', border: '3px solid var(--heritage-plum)' }} data-testid="invoice-dialog">
       <div className="h-eyebrow">Create invoice</div>
       <div className="h-display text-2xl mt-1" style={{ color: 'var(--heritage-plum)' }}>{invoiceDlg.name}</div>
       <div className="text-xs opacity-75 mt-1" style={{ color: 'var(--heritage-plum)' }}>{invoiceDlg.email}</div>
       <div className="mt-4 space-y-3">
         <div>
           <label className="block text-xs opacity-80 mb-1" style={{ color: 'var(--heritage-plum)' }}>Tier</label>
           <select value={invoice.tier_id} onChange={(e) => setInvoice({ ...invoice, tier_id: e.target.value })}
             className="w-full rounded-md px-3 py-2 text-sm"
             style={{ border: '1px solid rgba(75,24,56,0.3)', background: '#F8F4EE', color: 'var(--heritage-plum)' }}
             data-testid="invoice-tier">
             <option value="">Select a tier</option>
             {tiers.map((t) => <option key={t.id} value={t.id}>{t.name} — ${t.price} {t.currency || 'NZD'}</option>)}
           </select>
         </div>
         <div>
           <label className="block text-xs opacity-80 mb-1" style={{ color: 'var(--heritage-plum)' }}>Plan</label>
           <select value={invoice.plan_type} onChange={(e) => setInvoice({ ...invoice, plan_type: e.target.value })}
             className="w-full rounded-md px-3 py-2 text-sm"
             style={{ border: '1px solid rgba(75,24,56,0.3)', background: '#F8F4EE', color: 'var(--heritage-plum)' }}
             data-testid="invoice-plan">
             <option value="full">Pay in full</option>
             <option value="weekly_3">3 weekly payments (TrustBuy)</option>
             <option value="fortnightly_2">2 fortnightly payments (TrustBuy)</option>
           </select>
           <div className="text-[11px] opacity-70 mt-1" style={{ color: 'var(--heritage-plum)' }}>
             TrustBuy is only available on Bloom / Legacy tiers per policy — set to &ldquo;Pay in full&rdquo; for Seed.
           </div>
         </div>
       </div>
       <div className="mt-5 flex justify-end gap-2">
         <button onClick={() => setInvoiceDlg(null)} className="h-btn-outline text-xs">Cancel</button>
         <button onClick={submitInvoice} className="h-btn text-xs" data-testid="invoice-create">Create invoice</button>
       </div>
     </div>
   </div>
 )}

 {purgeDlg && (
   <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(75,24,56,0.5)' }} onClick={() => setPurgeDlg(false)}>
     <div className="w-full max-w-md rounded-2xl p-6" onClick={(e) => e.stopPropagation()}
       style={{ background: '#FBF8F1', border: '3px solid #9A2C2C' }} data-testid="purge-dialog">
       <div className="h-eyebrow" style={{ color: '#9A2C2C' }}>Danger zone</div>
       <div className="h-display text-2xl mt-1" style={{ color: 'var(--heritage-plum)' }}>Purge test data</div>
       <p className="text-sm mt-2 opacity-90" style={{ color: 'var(--heritage-plum)' }}>
         Deletes every application (and its user, payment, orders, memberships) whose email ends in a known test domain
         (@example.com, @test.com, @mailinator.com etc). This cannot be undone.
       </p>
       <div className="mt-3 rounded-lg p-3" style={{ background: 'rgba(75,24,56,0.05)', border: '1px solid rgba(75,24,56,0.15)' }}>
         <div className="h-eyebrow text-[10px]">Would delete</div>
         <div className="h-serif text-3xl mt-1" style={{ color: 'var(--heritage-plum)' }}>{purgePreview?.count ?? '...'} record{purgePreview?.count === 1 ? '' : 's'}</div>
         {purgePreview?.matched?.length > 0 && (
           <div className="mt-2 max-h-32 overflow-y-auto text-xs opacity-80" style={{ color: 'var(--heritage-plum)' }}>
             {purgePreview.matched.slice(0, 10).map((m) => <div key={m.id}>{m.email}</div>)}
             {purgePreview.matched.length > 10 && <div>... and {purgePreview.matched.length - 10} more</div>}
           </div>
         )}
       </div>
       <div className="mt-5 flex justify-end gap-2">
         <button onClick={() => setPurgeDlg(false)} className="h-btn-outline text-xs">Cancel</button>
         <button onClick={doPurge} className="h-btn text-xs" style={{ background: '#9A2C2C', color: '#F6F0E4' }} data-testid="purge-confirm">
           <Trash2 size={12} className="mr-1" /> Purge {purgePreview?.count || 0}
         </button>
       </div>
     </div>
   </div>
 )}
 </Panel>
 );
}

export function AdminPayments() {
  const [rows, setRows] = useState([]);
  const [confirm, setConfirm] = useState(null); // {id, ref, name, amount, currency, all}
  const [expanded, setExpanded] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [bulkDlg, setBulkDlg] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('pending');
  const [bulkRunning, setBulkRunning] = useState(false);
  const load = async () => { const { data } = await api.get('/admin/payments'); setRows(data.payments || []); };
  useEffect(() => { load(); }, []);
  const markPaidFull = async (id) => { await api.post('/admin/payments/mark-paid', { payment_id: id }); setConfirm(null); load(); };
  const markInstalmentPaid = async (id, idx) => { await api.post('/admin/payments/mark-instalment-paid', { payment_id: id, instalment_index: idx }); load(); };
  const markInstalmentUnpaid = async (id, idx) => {
    if (!window.confirm('Reverse this instalment verification?')) return;
    await api.post('/admin/payments/mark-instalment-unpaid', { payment_id: id, instalment_index: idx }); load();
  };
  const markUnpaid = async (id) => {
    if (!window.confirm('Reverse this verification? Founder will lose console access until re-verified.')) return;
    await api.post('/admin/payments/mark-unpaid', { payment_id: id }); load();
  };
  const removePayment = async (p) => {
    if (!window.confirm(`Delete payment ${p.reference || p.id} permanently? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/payments/${p.id}`);
      toast.success('Payment deleted');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };
  const resendInvoice = async (payment) => {
    setResendingId(payment.id);
    const tid = toast.loading(`Sending invoice to ${payment.applicant_email || payment.reference}...`);
    try {
      const { data } = await api.post(`/admin/payments/${payment.id}/resend-invoice`);
      toast.success(`Invoice ${payment.reference} delivered to ${data.email}`, { id: tid, duration: 5000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed', { id: tid });
    } finally { setResendingId(null); }
  };
  const runBulkResend = async () => {
    if (!confirm(`Send invoice emails to every ${bulkStatus === 'all' ? '' : bulkStatus + ' '}applicant now? This will hit their inbox immediately.`)) return;
    setBulkRunning(true);
    const tid = toast.loading(`Sending Heritage invoices... this can take up to a minute for larger batches. Do not close this tab.`, { duration: Infinity });
    try {
      const { data } = await api.post('/admin/payments/resend-invoices', { status: bulkStatus });
      const failedCount = (data.failed || []).length;
      toast.dismiss(tid);
      if (failedCount === 0) {
        toast.success(`Delivered ${data.sent} invoice${data.sent === 1 ? '' : 's'} to founder inboxes ✓`, { duration: 8000 });
      } else {
        toast.warning(`Delivered ${data.sent}/${data.attempted}. ${failedCount} failed — check the email log.`, { duration: 8000 });
      }
      setBulkDlg(false);
      await load();
    } catch (e) {
      toast.dismiss(tid);
      toast.error(e?.response?.data?.detail || 'Bulk send failed');
    } finally { setBulkRunning(false); }
  };
  const statusChip = (s) => {
    const map = {
      paid: { bg: 'rgba(143,163,75,0.15)', bd: 'rgba(143,163,75,0.4)', fg: '#5B6E2C' },
      pending: { bg: 'rgba(75,24,56,0.1)', bd: 'rgba(75,24,56,0.3)', fg: 'var(--heritage-plum)' },
    };
    const c = map[s] || map.pending;
    return <span className="h-chip capitalize" style={{ background: c.bg, borderColor: c.bd, color: c.fg }}>{s}</span>;
  };
  const fmtDue = (iso) => {
    try { const d = new Date(iso); return d.toLocaleDateString('en-NZ', { day: '2-digit', month: 'short' }); } catch { return ''; }
  };
  return (
    <Panel className="p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="h-eyebrow">Payments</div>
          <div className="h-display text-2xl mt-1" style={{ color: 'var(--heritage-plum)' }}>Founder Payment Verification</div>
          <div className="text-sm opacity-80 mt-1" style={{ color: 'var(--heritage-plum)' }}>
            Founders unlock the console once every instalment is verified. For Heritage Tab plans, tick each instalment as it arrives.
          </div>
        </div>
        <button onClick={() => setBulkDlg(true)} className="h-btn text-xs" data-testid="payments-bulk-resend">
          <Mail size={12} className="mr-1" /> Resend all invoices
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left h-eyebrow border-b" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
              <th className="py-2 pr-3">Reference</th>
              <th className="py-2 pr-3">Founder</th>
              <th className="py-2 pr-3">Tier</th>
              <th className="py-2 pr-3">Plan</th>
              <th className="py-2 pr-3">Progress</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3"></th>
            </tr>
          </thead>
          <tbody style={{ color: 'var(--heritage-plum)' }}>
            {rows.map((r) => {
              const isPlan = (r.plan_type && r.plan_type !== 'full') && (r.instalments || []).length > 0;
              const total = (r.instalments || []).length || 1;
              const paidCount = r.paid_instalments || 0;
              return (
                <React.Fragment key={r.id}>
                  <tr className="border-b align-top" style={{ borderColor: 'rgba(75,24,56,0.08)' }} data-testid={`payment-row-${r.reference}`}>
                    <td className="py-3 pr-3 h-serif">{r.reference}</td>
                    <td className="py-3 pr-3">
                      <div className="h-serif">{r.applicant_name || '\u2014'}</div>
                      <div className="text-xs opacity-70">{r.applicant_email || ''}</div>
                      {r.last_email_sent_at && (
                        <div className="text-[10px] opacity-60 mt-0.5 flex items-center gap-1" style={{ color: 'var(--heritage-plum)' }}>
                          <Mail size={9} />
                          Last email: {new Date(r.last_email_sent_at).toLocaleString('en-NZ', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </div>
                      )}
                    </td>
                    <td className="py-3 pr-3">{r.tier_name || '\u2014'}</td>
                    <td className="py-3 pr-3 text-xs">
                      {isPlan ? (
                        <div className="h-serif text-sm">{r.plan_label}</div>
                      ) : (
                        <span className="opacity-70">Pay in full</span>
                      )}
                    </td>
                    <td className="py-3 pr-3">
                      {isPlan ? (
                        <div>
                          <div className="text-xs opacity-70">{paidCount} of {total} received</div>
                          <div className="w-32 h-1.5 rounded-full mt-1 overflow-hidden" style={{ background: 'rgba(75,24,56,0.15)' }}>
                            <div className="h-full" style={{ width: `${(paidCount / total) * 100}%`, background: 'var(--heritage-plum)' }} />
                          </div>
                        </div>
                      ) : (
                        <div className="h-serif">${r.amount} {r.currency}</div>
                      )}
                    </td>
                    <td className="py-3 pr-3">{statusChip(r.status)}</td>
                    <td className="py-3 pr-3 whitespace-nowrap">
                      {isPlan && (
                        <button className="h-btn-outline text-xs mr-1" onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                                data-testid={`payment-expand-${r.reference}`}>
                          {expanded === r.id ? 'Hide' : 'Details'}
                        </button>
                      )}
                      {r.status !== 'paid' ? (
                        <button className="h-btn text-xs" onClick={() => setConfirm({ id: r.id, ref: r.reference, name: r.applicant_name, amount: r.amount, currency: r.currency, all: true })}
                                data-testid={`payment-mark-paid-${r.reference}`}>
                          Mark all paid
                        </button>
                      ) : (
                        <button className="h-btn-outline text-xs" onClick={() => markUnpaid(r.id)} data-testid={`payment-unmark-${r.reference}`}>
                          Reverse
                        </button>
                      )}
                      <button className="h-btn-outline text-xs ml-1" onClick={() => resendInvoice(r)} disabled={resendingId === r.id}
                              data-testid={`payment-resend-${r.reference}`} title="Resend invoice email">
                        {resendingId === r.id ? '...' : <Mail size={12} />}
                      </button>
                      <button className="text-xs ml-1 opacity-80 hover:opacity-100 inline-flex items-center gap-1 rounded-md px-2 py-1"
                              onClick={() => removePayment(r)} style={{ color: '#9A2C2C' }}
                              data-testid={`payment-delete-${r.reference}`} title="Delete payment permanently">
                        <Trash2 size={12} />
                      </button>
                    </td>
                  </tr>
                  {isPlan && expanded === r.id && (
                    <tr>
                      <td colSpan={7} className="p-3" style={{ background: 'rgba(75,24,56,0.03)' }}>
                        <div className="text-xs h-eyebrow mb-2">Instalments</div>
                        <div className="rounded-lg overflow-hidden" style={{ border: '1px solid rgba(75,24,56,0.15)' }}>
                          <table className="w-full text-xs" style={{ color: 'var(--heritage-plum)' }}>
                            <thead style={{ background: 'rgba(75,24,56,0.06)' }}>
                              <tr>
                                <th className="text-left p-2">#</th>
                                <th className="text-left p-2">Due</th>
                                <th className="text-left p-2">Amount</th>
                                <th className="text-left p-2">Ref</th>
                                <th className="text-left p-2">Status</th>
                                <th className="text-left p-2"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {(r.instalments || []).map((inst, i) => {
                                const isPaid = inst.status === 'paid';
                                return (
                                  <tr key={inst.reference || `pi-${i}`} className="border-t" style={{ borderColor: 'rgba(75,24,56,0.08)' }} data-testid={`payment-inst-${r.reference}-${i}`}>
                                    <td className="p-2 h-serif">{i + 1}</td>
                                    <td className="p-2">{fmtDue(inst.due_date)}{inst.paused && <span className="ml-1 opacity-70">(paused)</span>}</td>
                                    <td className="p-2 h-serif">${inst.amount} {r.currency}</td>
                                    <td className="p-2">{inst.reference}</td>
                                    <td className="p-2">{statusChip(isPaid ? 'paid' : 'pending')}</td>
                                    <td className="p-2">
                                      {isPaid ? (
                                        <button className="h-btn-outline text-xs" onClick={() => markInstalmentUnpaid(r.id, i)}>Reverse</button>
                                      ) : (
                                        <button className="h-btn text-xs" onClick={() => markInstalmentPaid(r.id, i)}
                                                data-testid={`payment-inst-mark-${r.reference}-${i}`}>
                                          Mark paid
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {rows.length === 0 && <div className="text-sm opacity-70 mt-4" style={{ color: 'var(--heritage-plum)' }}>No payments yet.</div>}

      {/* Confirmation modal */}
      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
             style={{ background: 'rgba(75,24,56,0.4)', backdropFilter: 'blur(4px)' }}
             onClick={() => setConfirm(null)}>
          <div className="h-panel max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="h-eyebrow">Confirm all payments received</div>
            <div className="h-display text-2xl mt-2" style={{ color: 'var(--heritage-plum)' }}>Mark plan fully paid?</div>
            <div className="text-sm mt-3 opacity-90" style={{ color: 'var(--heritage-plum)' }}>
              This flips every instalment (if any) to paid and unlocks the Founders Console for <strong>{confirm.name || confirm.ref}</strong>. This can be reversed later.
            </div>
            <div className="mt-4 rounded-lg p-4 text-sm" style={{ background: '#FBF8F1', border: '1px solid rgba(75,24,56,0.15)', color: 'var(--heritage-plum)' }}>
              <div><span className="h-eyebrow text-[10px] mr-2">Reference</span><span className="h-serif">{confirm.ref}</span></div>
              <div className="mt-1"><span className="h-eyebrow text-[10px] mr-2">Total</span><span className="h-serif">${confirm.amount} {confirm.currency}</span></div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button className="h-btn-outline text-xs" onClick={() => setConfirm(null)} data-testid="payment-confirm-cancel">Cancel</button>
              <button className="h-btn text-xs" onClick={() => markPaidFull(confirm.id)} data-testid="payment-confirm-yes">Yes, mark all paid</button>
            </div>
          </div>
        </div>
      )}
      {bulkDlg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(75,24,56,0.5)' }} onClick={() => setBulkDlg(false)}>
          <div className="w-full max-w-md rounded-2xl p-6" onClick={(e) => e.stopPropagation()}
               style={{ background: '#FBF8F1', border: '3px solid var(--heritage-plum)' }} data-testid="payments-bulk-dialog">
            <div className="h-eyebrow">Bulk resend invoices</div>
            <div className="h-display text-2xl mt-1" style={{ color: 'var(--heritage-plum)' }}>Email every applicant</div>
            <p className="text-sm mt-2 opacity-90" style={{ color: 'var(--heritage-plum)' }}>
              Sends the Heritage invoice email (bank details, reference, and TrustBuy schedule if applicable) to the payer&rsquo;s inbox. Use this now that Resend is live to catch anyone who applied before email delivery was hooked up.
            </p>
            <div className="mt-4">
              <label className="block text-xs opacity-80 mb-1" style={{ color: 'var(--heritage-plum)' }}>Which payments?</label>
              <select value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value)}
                className="w-full rounded-md px-3 py-2 text-sm"
                style={{ border: '1px solid rgba(75,24,56,0.3)', background: '#F8F4EE', color: 'var(--heritage-plum)' }}
                data-testid="payments-bulk-status">
                <option value="pending">Pending only (recommended — chases the unpaid)</option>
                <option value="all">All payments</option>
                <option value="paid">Paid only (rare — for reprints)</option>
              </select>
            </div>
            <div className="text-[11px] opacity-70 mt-3" style={{ color: 'var(--heritage-plum)' }}>
              Emails send immediately. There is no undo.
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setBulkDlg(false)} className="h-btn-outline text-xs" data-testid="payments-bulk-cancel">Cancel</button>
              <button onClick={runBulkResend} disabled={bulkRunning} className="h-btn text-xs" data-testid="payments-bulk-run">
                <Mail size={12} className="mr-1" /> {bulkRunning ? 'Sending…' : 'Send now'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Panel>
  );
}

export function AdminProduction() {
 const [items, setItems] = useState([]);
 const load = async () => { const { data } = await api.get('/admin/production'); setItems(data.items || []); };
 useEffect(() => { load(); }, []);
 const advance = async (user_id, stage_key) => { await api.post('/admin/production/advance', { user_id, stage_key }); load(); };
 return (
 <div className="space-y-6">
 {items.map(it => (
 <Panel key={it.membership?.id} className="p-6">
 <div className="flex items-center justify-between">
 <div>
 <div className="h-eyebrow">{it.membership?.founder_number}</div>
 <div className="h-serif text-xl" style={{ color: 'var(--heritage-plum)' }}>{it.user?.name}</div>
 <div className="text-xs opacity-80" style={{ color: 'var(--heritage-plum)' }}>{it.membership?.tier_name}</div>
 </div>
 </div>
 <div className="grid md:grid-cols-2 gap-2 mt-4">
 {it.stages.map(s => (
 <div key={s.id} className="flex items-center justify-between p-2 rounded-lg" style={{ background: s.completed ? 'rgba(143,163,75,0.15)' : 'rgba(75,24,56,0.05)' }}>
 <div>
 <div className="h-serif" style={{ color: 'var(--heritage-plum)' }}>{s.stage_label}</div>
 <div className="h-eyebrow">{s.completed ? 'Completed' : 'Pending'}</div>
 </div>
 {!s.completed && <button className="h-btn text-xs" onClick={() => advance(it.user.id, s.stage_key)}>Advance</button>}
 </div>
 ))}
 </div>
 </Panel>
 ))}
 {items.length === 0 && <div className="text-sm opacity-70" style={{ color: 'var(--heritage-plum)' }}>No founder memberships yet.</div>}
 </div>
 );
}

export function AdminColourways() {
 const [rows, setRows] = useState([]);
 const [form, setForm] = useState({ name: '', hex: '#9A4B7E', tier_restricted: false });
 const load = async () => { const { data } = await api.get('/admin/colourways'); setRows(data.colourways || []); };
 useEffect(() => { load(); }, []);
 const add = async (e) => { e.preventDefault(); await api.post('/admin/colourways', form); setForm({ name: '', hex: '#9A4B7E', tier_restricted: false }); load(); };
 const del = async (id) => { await api.delete(`/admin/colourways/${id}`); load(); };
 return (
 <div className="space-y-6">
 <Panel className="p-6">
 <div className="h-eyebrow mb-3">Add colourway</div>
 <form onSubmit={add} className="grid md:grid-cols-4 gap-3">
 <input className="h-input" required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
 <input className="h-input" required type="color" value={form.hex} onChange={(e) => setForm({ ...form, hex: e.target.value })} />
 <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--heritage-plum)' }}><input type="checkbox" checked={form.tier_restricted} onChange={(e) => setForm({ ...form, tier_restricted: e.target.checked })} /> Founder only</label>
 <button className="h-btn">Add</button>
 </form>
 </Panel>
 <div className="grid md:grid-cols-3 gap-3">
 {rows.map(r => (
 <Panel key={r.id} className="p-4">
 <div className="flex items-center gap-3">
 <div className="w-12 h-12 rounded-lg" style={{ background: r.hex }} />
 <div>
 <div className="h-serif" style={{ color: 'var(--heritage-plum)' }}>{r.name}</div>
 <div className="h-eyebrow">{r.tier_restricted ? 'Founder only' : 'Public'}</div>
 </div>
 <button className="h-btn-outline text-xs ml-auto" onClick={() => del(r.id)}>Delete</button>
 </div>
 </Panel>
 ))}
 </div>
 </div>
 );
}

export function AdminProducts() {
 const [rows, setRows] = useState([]);
 const [form, setForm] = useState({ name: '', description: '', price: 0, category: 'apparel', founder_vault: false, active: true });
 const load = async () => { const { data } = await api.get('/admin/products'); setRows(data.products || []); };
 useEffect(() => { load(); }, []);
 const add = async (e) => { e.preventDefault(); await api.post('/admin/products', form); setForm({ name: '', description: '', price: 0, category: 'apparel', founder_vault: false, active: true }); load(); };
 const del = async (id) => { await api.delete(`/admin/products/${id}`); load(); };
 return (
 <div className="space-y-6">
 <Panel className="p-6">
 <div className="h-eyebrow mb-3">Add product</div>
 <form onSubmit={add} className="grid md:grid-cols-2 gap-3">
 <input className="h-input" required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
 <input className="h-input" required placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
 <input className="h-input" required type="number" placeholder="Price" value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) })} />
 <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--heritage-plum)' }}><input type="checkbox" checked={form.founder_vault} onChange={(e) => setForm({ ...form, founder_vault: e.target.checked })} /> Founder Vault</label>
 <textarea className="h-input md:col-span-2" rows={2} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
 <button className="h-btn md:col-span-2">Add product</button>
 </form>
 </Panel>
 <div className="grid md:grid-cols-2 gap-3">
 {rows.map(r => (
 <Panel key={r.id} className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <div className="h-serif text-lg" style={{ color: 'var(--heritage-plum)' }}>{r.name}</div>
 <div className="text-xs opacity-80" style={{ color: 'var(--heritage-plum)' }}>{r.category} • ${r.price}</div>
 {r.founder_vault && <div className="h-eyebrow">Founder Vault</div>}
 </div>
 <button className="h-btn-outline text-xs" onClick={() => del(r.id)}>Delete</button>
 </div>
 </Panel>
 ))}
 </div>
 </div>
 );
}
